import { Button } from '../../components/ui'
import { useAppSelector } from '../../store/hooks'
import { useTenant } from '../../tenant/useTenant'
import { selectSubtotal, selectTotal, selectTotalDuration } from '../../features/booking/selectionSlice'
import { formatDuration, formatPrice } from '../../utils/format'
import type { WizardAction } from './wizardContext'

interface SummaryBarProps {
  action: WizardAction | null
  /** Shown when there is somewhere to go back to and edit. */
  onEdit?: () => void
}

/**
 * Sticky at the bottom on every step: what it costs, how long it takes, and the
 * single way forward. Stitch weights the running total as a headline in Oroshi
 * Blue against a label-caps eyebrow, and gives the primary action the full width
 * of the phone before letting it shrink to its own box on wider screens.
 */
export function SummaryBar({ action, onEdit }: SummaryBarProps) {
  const selection = useAppSelector((s) => s.selection)
  const { bootstrap } = useTenant()

  const count = selection.services.length
  const duration = selectTotalDuration(selection)
  const subtotal = selectSubtotal(selection)
  const total = selectTotal(selection)
  const discounted = total !== subtotal

  return (
    // Opaque, not a frosted panel: this bar floats over the step content while
    // the page scrolls, and at 80% the cards behind it showed straight through
    // — two sets of prices and headings reading as one jumbled row.
    <div className="sticky bottom-0 z-40 border-t border-outline-variant bg-surface shadow-[0_-1px_12px_rgba(0,0,0,0.06)]">
      <div className="container-page flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2 font-label-caps text-label-caps uppercase text-on-surface-variant">
            <span>
              {count === 0
                ? 'No services yet'
                : `${count} ${count === 1 ? 'service' : 'services'} · ${formatDuration(duration)}`}
            </span>
            {onEdit && count > 0 && (
              <>
                <span aria-hidden="true" className="text-outline-variant">
                  ·
                </span>
                <button
                  type="button"
                  onClick={onEdit}
                  className="font-label-caps text-label-caps uppercase text-primary underline transition-colors hover:text-primary-container"
                >
                  Edit
                </button>
              </>
            )}
          </p>
          <p className="mt-1 flex items-baseline gap-3 font-display text-headline-sm text-primary">
            {formatPrice(total, bootstrap.currency)}
            {discounted && (
              <span className="font-body-md text-body-md text-on-surface-variant line-through">
                {formatPrice(subtotal, bootstrap.currency)}
              </span>
            )}
          </p>
        </div>

        {action && (
          <Button
            size="lg"
            onClick={action.onClick}
            disabled={action.disabled}
            loading={action.loading}
            className="w-full shrink-0 sm:w-auto"
          >
            {action.label}
          </Button>
        )}
      </div>
    </div>
  )
}
